// MainLayout.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import LeftNavBar from './LeftNavBar';
import MercenaryBar from './MercenaryBar';
import Header from '../Shared/Header';
import MobileHeader from './MobileHeader';
import TutorialOverlay from '../Tutorial/TutorialOverlay';
import GlobalNotification, { NotificationPayload, AdvancementPayload, AnnouncementPayload } from '../Shared/GlobalNotification';
import FeedbackPopup from '../Feedback/FeedbackPopup';
import './MainLayout.css';
import styles from './MainLayout.module.css';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { mercenary, setMercenaryId } = useMercenary();
  const { user } = useAuth();
  const { socket } = useSocket();
  const [notificationEvents, setNotificationEvents] = useState<NotificationPayload[]>([]);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );

  // Feedback popup state
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [forceOpenFeedback, setForceOpenFeedback] = useState(false);
  const [deferredFeedback, setDeferredFeedback] = useState<{ formId: number; title: string } | null>(null);
  const feedbackCheckedRef = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleAdvancement = (payload: any) => {
      const mapped: AdvancementPayload = { ...payload, kind: 'advancement' };
      setNotificationEvents((prev) => {
        const next = [...prev, mapped];
        return next.length > 100 ? next.slice(-100) : next;
      });
    };

    const handleAnnouncement = (payload: any) => {
      const mapped: AnnouncementPayload = { ...payload, kind: 'announcement' };
      setNotificationEvents((prev) => {
        const next = [...prev, mapped];
        return next.length > 100 ? next.slice(-100) : next;
      });
    };

    socket.on('mercenary_advancement', handleAdvancement);
    socket.on('global_announcement', handleAnnouncement);
    return () => {
      socket.off('mercenary_advancement', handleAdvancement);
      socket.off('global_announcement', handleAnnouncement);
    };
  }, [socket]);

  useEffect(() => {
    if (mercenary) {
      setMercenaryId(mercenary.id);
    } else {
      setMercenaryId(null);
    }
    // setMercenaryId is stable from context, no need to include
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mercenary]);

  // Check for pending feedback once on mount (after user logs in)
  useEffect(() => {
    if (!user || feedbackCheckedRef.current) return;
    feedbackCheckedRef.current = true;
    // Directly set the header button as soon as a pending form is found.
    // This is independent of the popup callback chain so the button always
    // appears even if the popup's async flow encounters issues.
    axios.get('/api/feedback/pending', { withCredentials: true })
      .then(res => {
        if (res.data && res.data.length > 0) {
          const form = res.data[0];
          setDeferredFeedback({ formId: form.id, title: form.title });
        }
      })
      .catch(() => {});
    const timer = setTimeout(() => setShowFeedbackPopup(true), 1500);
    return () => clearTimeout(timer);
  }, [user]);

  // Listen for new feedback forms via socket
  useEffect(() => {
    if (!socket) return;
    const handleNewFeedback = () => {
      setShowFeedbackPopup(true);
    };
    socket.on('newFeedbackForm', handleNewFeedback);
    return () => { socket.off('newFeedbackForm', handleNewFeedback); };
  }, [socket]);

  const handleFeedbackDismiss = useCallback(() => {
    setShowFeedbackPopup(false);
    setForceOpenFeedback(false);
    setDeferredFeedback(null);
  }, []);

  const handleFeedbackDefer = useCallback((formId: number, title: string) => {
    setShowFeedbackPopup(false);
    setForceOpenFeedback(false);
    setDeferredFeedback({ formId, title });
  }, []);

  // Called as soon as any pending form is found — shows header button without closing popup
  const handleFeedbackFormFound = useCallback((formId: number, title: string) => {
    setDeferredFeedback({ formId, title });
  }, []);

  const handleOpenDeferredFeedback = useCallback(() => {
    setForceOpenFeedback(true);
    setDeferredFeedback(null);
    setShowFeedbackPopup(true);
  }, []);

  return (
    <TutorialOverlay>
      <div className={styles.layoutContainer}>
        {isMobileViewport ? (
          <MobileHeader user={user} />
        ) : (
          <Header user={user} pendingFeedback={deferredFeedback} onOpenFeedback={handleOpenDeferredFeedback} />
        )}
        {/* Global notification overlay (advancements + announcements, unified queue) */}
        <GlobalNotification events={notificationEvents} />
        {/* Feedback popup overlay */}
        {showFeedbackPopup && user && (
          <FeedbackPopup onDismiss={handleFeedbackDismiss} onDefer={handleFeedbackDefer} onFormFound={handleFeedbackFormFound} forceOpen={forceOpenFeedback} />
        )}
        <div className={styles.mainContent}>
          {user && mercenary && <LeftNavBar />}
          <div className={styles.contentArea} key={mercenary?.id ?? 'none'}>
            {children}
          </div>
          {user && mercenary && (
            <MercenaryBar />
          )}
        </div>
        {/* <Footer /> */}
      </div>
    </TutorialOverlay>
  );
};

export default MainLayout;
